/**
 * Image rendering — decodes HTMLImageElement on first use, uploads to a GPU
 * texture, caches per (node.id, url, w, h). Mirrors textLayout's getTextTexture
 * and the SVG-path cache in the same file.
 *
 * Async strategy:
 *  - On the first getImageTexture() call for a node, kick off
 *    `new Image(); img.src = url; await img.decode()`.
 *  - Returns the cached entry (possibly placeholder) immediately. The node's
 *    texture is wired up via the same drawRects path as text — the rect FS
 *    samples uFillTex using vCorner as UV, with uUseFillTex=1.
 *  - When decode() resolves, the next frame's compile re-calls getImageTexture
 *    (cache key includes the natural image size), which sees the ready
 *    HTMLImageElement and uploads via texImage2D. From then on, cache hits.
 *
 * For the MVP we do NOT block the compile path. Until decode resolves, the
 * image node renders as solid white (fallback). That's fine for the brief —
 * "render real raster images" — the decode happens in ~1 frame for cached
 * network images and a few frames for cold loads.
 *
 * background-image: url(...) is supported in MVP form: a rect with that
 * declaration becomes an "image" node where imageUrl is the URL and the
 * texture is stretched to fill (background-size: cover equivalent). No
 * background-position/repeat/tile support — call it a stretch.
 */

import type { SceneNode } from "./sceneGraph";

interface ImageEntry {
  texture: WebGLTexture;
  width: number;     // image natural width in pixels
  height: number;    // image natural height in pixels
  cssWidth: number;  // for layout — equals n.w
  cssHeight: number;
  key: string;
  /** Internal HTMLImageElement, populated as soon as decode resolves. */
  img: HTMLImageElement | null;
}

const cache = new Map<number, ImageEntry>();
/** In-flight load promises so we don't request the same URL twice in parallel. */
const inflight = new Map<string, Promise<HTMLImageElement>>();

function keyOf(n: SceneNode): string {
  return [
    "image",
    n.imageUrl ?? "",
    n.w,
    n.h,
  ].join("|");
}

function loadImage(url: string): Promise<HTMLImageElement> {
  const existing = inflight.get(url);
  if (existing) return existing;
  const p = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    // Don't set crossOrigin for data: URLs — they don't need CORS and some
    // browsers (Safari especially) will silently fail to decode when
    // crossOrigin is set on a same-origin URL.
    if (!url.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      // img.decode() is preferred where available — it waits for the decode
      // step to finish, not just the network load. Safari 14.1+ supports it.
      if (typeof img.decode === "function") {
        img.decode().then(() => resolve(img)).catch(() => resolve(img));
      } else {
        resolve(img);
      }
      inflight.delete(url);
    };
    img.onerror = (e) => {
      inflight.delete(url);
      reject(e);
    };
    img.src = url;
  });
  inflight.set(url, p);
  return p;
}

export function getImageTexture(
  gl: WebGL2RenderingContext,
  n: SceneNode,
): ImageEntry | null {
  // Accept both explicit image nodes AND rect nodes that carry an
  // imageUrl — a rect with imageUrl is a "filled" rect, common in the
  // product gallery + scene-graph scenes. The image texture pipeline
  // treats them identically.
  if (!n.imageUrl) return null;
  if (n.type !== "image" && n.type !== "rect") return null;
  const url = n.imageUrl;
  const key = keyOf(n);

  const existing = cache.get(n.id);
  if (existing && existing.key === key && existing.img && existing.width > 0) {
    // Re-upload only if dimensions changed (e.g. node resized). Most frames
    // hit the cache.
    return existing;
  }

  // Kick off decode (or reuse inflight). We do NOT await it — we return
  // immediately with whatever we have. The next frame will see the loaded
  // image and re-upload.
  loadImage(url)
    .then((img) => {
      // Find the node in the cache; if its key still matches the URL+w+h we
      // last saw, upload now. If the node changed (different size/URL), the
      // next getImageTexture() call will do it.
      const entry = cache.get(n.id);
      if (!entry || entry.key !== key) return;
      if (!img.naturalWidth || !img.naturalHeight) {
        // SVG didn't decode to a usable raster — leave the placeholder.
        // (happens with malformed data: URLs in some browsers.)
        return;
      }
      uploadToTexture(gl, entry, img);
    })
    .catch(() => {
      // Image failed to load; leave the placeholder white texture. Future
      // calls will re-attempt only if the URL changes.
    });

  // Create / reuse a texture object. We bind a 1×1 white pixel as a
  // placeholder so the draw call still renders something sensible (solid
  // white) until the image loads.
  let entry = existing;
  if (!entry) {
    const tex = gl.createTexture();
    if (!tex) return null;
    entry = { texture: tex, width: 0, height: 0, cssWidth: n.w, cssHeight: n.h, key, img: null };
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([255, 255, 255, 255]),
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  } else if (entry.key !== key) {
    // Key changed — reset metadata, keep the texture object to avoid GL churn.
    entry.key = key;
    entry.width = 0;
    entry.height = 0;
    entry.img = null;
  }
  cache.set(n.id, entry);
  return entry;
}

/** Shared scratch canvas for image rasterization. Reusing a single canvas
 *  avoids GC pressure; we resize it as needed before each draw. */
const scratchCanvas = (() => {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = 1;
  c.height = 1;
  return c;
})();

function uploadToTexture(
  gl: WebGL2RenderingContext,
  entry: ImageEntry,
  img: HTMLImageElement,
): void {
  // Draw the HTMLImageElement into a 2D canvas first. Direct texImage2D from
  // an HTMLImageElement can fail in some browsers (and produces a texture
  // that's not always framebuffer-attachable). Canvas-copy is the well-trodden
  // path used by textLayout's bake step.
  if (!scratchCanvas) {
    uploadFromImage(gl, entry, img);
    return;
  }
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (scratchCanvas.width !== w) scratchCanvas.width = w;
  if (scratchCanvas.height !== h) scratchCanvas.height = h;
  const ctx2d = scratchCanvas.getContext("2d");
  if (!ctx2d) {
    uploadFromImage(gl, entry, img);
    return;
  }
  ctx2d.clearRect(0, 0, w, h);
  ctx2d.drawImage(img, 0, 0, w, h);
  gl.bindTexture(gl.TEXTURE_2D, entry.texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, scratchCanvas);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  entry.width = w;
  entry.height = h;
  entry.cssWidth = entry.cssWidth;
  entry.cssHeight = entry.cssHeight;
  entry.img = img;
}

function uploadFromImage(
  gl: WebGL2RenderingContext,
  entry: ImageEntry,
  img: HTMLImageElement,
): void {
  gl.bindTexture(gl.TEXTURE_2D, entry.texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  entry.width = img.naturalWidth || img.width;
  entry.height = img.naturalHeight || img.height;
  entry.cssWidth = entry.cssWidth;
  entry.cssHeight = entry.cssHeight;
  entry.img = img;
}

/**
 * uploadLiveCanvasTexture — bypasses the URL-based cache. Use this when you
 * have a canvas (e.g. a live `<video>` element's current frame) that needs
 * to be uploaded as the texture for a specific node. Re-uploads every call.
 *
 * One texture per node.id. If multiple nodes share the same canvas source,
 * call this for each of them. For 64 tiles driven by the same video frame
 * this means 64 texture uploads per frame — fine for a demo, expensive for
 * a production app (which would use one big shared texture and per-tile UV
 * offsets in the FS, not yet supported by the rect pipeline).
 *
 * Returns true if the texture was uploaded successfully.
 */
export function uploadLiveCanvasTexture(
  gl: WebGL2RenderingContext,
  nodeId: number,
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
): boolean {
  if (!canvas.width || !canvas.height) return false;
  let entry = cache.get(nodeId);
  if (!entry) {
    const tex = gl.createTexture();
    if (!tex) return false;
    entry = {
      texture: tex,
      width: canvas.width,
      height: canvas.height,
      cssWidth,
      cssHeight,
      key: `live:${nodeId}`,
      img: null,
    };
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([255, 255, 255, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    cache.set(nodeId, entry);
  }
  gl.bindTexture(gl.TEXTURE_2D, entry.texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
  entry.width = canvas.width;
  entry.height = canvas.height;
  entry.cssWidth = cssWidth;
  entry.cssHeight = cssHeight;
  return true;
}

export function clearImageCache(gl: WebGL2RenderingContext): void {
  for (const e of cache.values()) gl.deleteTexture(e.texture);
  cache.clear();
}